import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './axiosInstance'
import { useAuthStore } from '../store/authStore'
import type { AuthUser } from '../store/authStore'

interface AuthModeResponse {
  mode: 'local' | 'oidc'
  oidcIssuer: string | null
}

interface LoginResponse {
  accessToken: string
  user: AuthUser
}

export function useAuthMode() {
  const setAuthMode = useAuthStore((s) => s.setAuthMode)
  const setLoading = useAuthStore((s) => s.setLoading)

  return useQuery({
    queryKey: ['authMode'],
    queryFn: async () => {
      const res = await api.get<AuthModeResponse>('/auth/mode')
      setAuthMode(res.data.mode)
      setLoading(false)
      return res.data
    },
    staleTime: Infinity,
    retry: 1,
  })
}

export function useLogin() {
  const setAccessToken = useAuthStore((s) => s.setAccessToken)
  const setUser = useAuthStore((s) => s.setUser)

  return useMutation({
    mutationFn: async (creds: { username: string; password: string }) => {
      const res = await api.post<LoginResponse>('/auth/login', creds)
      return res.data
    },
    onSuccess: (data) => {
      setAccessToken(data.accessToken)
      setUser(data.user)
    },
  })
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout')
    },
    onSettled: () => {
      logout()
      qc.clear()
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      await api.patch('/auth/password', data)
    },
  })
}
